// frontend/src/pages/MatchScoring/WagonWheelModal.js
//
// Detail picker for a delivery's direction. Returns:
//   { zoneSector, zoneRing, shotType, fielderId, incident, commentaryText }
//
// onSkip just commits without these details (caller should still create
// the ball with whatever defaults it had pre-modal).
//
// `delivery` is the in-flight delivery payload from ScoringPad / ExtrasModal,
// used so the live commentary preview can compose the right verb.

import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { SECTORS, composeCommentary, applyWheelConfig, shotTypeList, shotsForSector, sectorChips } from './commentary';

function polar(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function pieSlicePath(cx, cy, r, startDeg, endDeg) {
  const start = polar(cx, cy, r, endDeg);
  const end   = polar(cx, cy, r, startDeg);
  const large = (endDeg - startDeg) <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y} Z`;
}

const INCIDENTS = [
  { v: 'none',          label: '— none —' },
  { v: 'drop',          label: 'Dropped catch' },
  { v: 'missed_runout', label: 'Missed run-out' },
  { v: 'misfield',      label: 'Misfield' },
];

export default function WagonWheelModal({
  delivery,             // { kind, runs, offTheBat, ... } from earlier in flow
  batterName,
  bowlerName,
  bowlingSquad,
  membersById,
  onSkip,
  onConfirm,
}) {
  const [hand, setHand]         = useState('RH');
  const [sector, setSector]     = useState(null);
  const [ring, setRing]         = useState('circle');
  const [shotType, setShotType] = useState('drive');
  const [fielderId, setFielder] = useState('');
  const [incident, setIncident] = useState('none');
  const [comm, setComm]         = useState('');
  const [overridden, setOverridden] = useState(false);
  // Push 3 v3 (Session 21 — 26 May 2026): when true, ignore the
  // SHOTS_BY_SECTOR filter and show all 12 shot types ("+ other" tapped).
  const [showAllShots, setShowAllShots] = useState(false);
  // Push 3 v3: which "side hint" the scorer picked for boundary on the
  // Straight slice. Long Off → 'off', Long On → 'leg', Straight Boundary → null.
  // Stored alongside (zone_sector, zone_ring) so wagon-wheel viz can render
  // the right label. Cleared whenever sector changes.
  const [sideHint, setSideHint] = useState(null);

  // Load admin-managed custom shots/positions once, then re-render so the
  // wheel includes them. Safe: on any failure we keep the built-in defaults.
  const [, bumpCfg] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get('/scorer/wheel-config')
      .then(res => { if (alive) { applyWheelConfig(res.data || {}); bumpCfg(v => v + 1); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // SVG layout
  const cx = 140, cy = 140, R = 110;

  const zonePaths = useMemo(() => {
    const flip = (hand === 'LH');
    return SECTORS.map((s, i) => {
      // Push 3 v3 (Session 21 — 26 May 2026): 20° half-width for the
      // 9-zone wheel (40° slices) — adds Mid-On between Midwicket and Straight.
      const half = 20;
      let startDeg = s.c - half;
      let endDeg   = s.c + half;
      if (flip) {
        const ns = -endDeg, ne = -startDeg;
        startDeg = ns; endDeg = ne;
      }
      const midDeg = (startDeg + endDeg) / 2;
      const labelPos = polar(cx, cy, R * 0.65, midDeg);
      return {
        i, side: s.side,
        d: pieSlicePath(cx, cy, R, startDeg, endDeg),
        label: s.circle && s.circle !== '—' ? s.circle : null,
        labelX: labelPos.x, labelY: labelPos.y + 3,
      };
    });
  }, [hand]);

  // Recompose commentary on any change unless the user manually edited it
  useEffect(() => {
    if (overridden) return;
    const fielderName = fielderId ? (membersById.get(fielderId)?.display_name || '') : null;
    const composed = composeCommentary({
      kind:        delivery.kind || 'bat',
      runs:        delivery.runs || 0,
      offTheBat:   delivery.offTheBat || 0,
      zoneSector:  sector,
      zoneRing:    ring,
      zoneSideHint: sideHint,
      shotType,
      fielderName: fielderName || null,
      incident:    incident === 'none' ? null : incident,
      batterName,
      bowlerName,
    });
    setComm(composed);
  }, [delivery, sector, ring, sideHint, shotType, fielderId, incident, hand, batterName, bowlerName, membersById, overridden]);

  function selectSector(i) {
    setSector(i);
    setSideHint(null);
    setShowAllShots(false);
    // Default ring to 'circle' for the new zone. If circle is disabled
    // for that zone (e.g. — for some special-case future zone), fall back
    // to the first available depth.
    const s = SECTORS[i];
    if (s) {
      const disabled = (key) => s[key] === '—' || s[key] == null;
      if (!disabled('circle'))         setRing('circle');
      else if (!disabled('boundary'))  setRing('boundary');
      else if (!disabled('inner'))     setRing('inner');
    }
  }

  function confirm() {
    onConfirm({
      zoneSector: sector,
      zoneRing:   sector !== null ? ring : null,
      zoneSideHint: sector !== null ? sideHint : null,
      shotType:   (delivery.kind === 'bat' || (delivery.kind === 'nb' && delivery.offTheBat > 0)) ? shotType : null,
      fielderId:  fielderId || null,
      incident:   incident === 'none' ? null : incident,
      commentaryText:   comm,
      commentaryOverridden: overridden,
    });
  }

  const titleSuffix = delivery.kind === 'wd' ? `Wide ${delivery.runs}`
                    : delivery.kind === 'nb' ? `No-ball${delivery.offTheBat > 0 ? ` + ${delivery.offTheBat} off bat` : ''}`
                    : delivery.kind === 'b'  ? `${delivery.runs} bye${delivery.runs === 1 ? '' : 's'}`
                    : delivery.kind === 'lb' ? `${delivery.runs} leg-bye${delivery.runs === 1 ? '' : 's'}`
                    : `${delivery.runs} run${delivery.runs === 1 ? '' : 's'}`;

  return (
    <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onSkip()}>
      <div className="sc-modal">
        <div className="sc-modal-body">
        <h3>Shot detail · {titleSuffix}</h3>
        <h4>Where did it go? <span style={{ color: 'var(--sc-gold-mid)', fontWeight: 700 }}>tap zone</span></h4>

        <div className="sc-wheel-wrap">
          <div className="sc-wheel">
            <svg viewBox="0 0 280 280">
              <g>
                {zonePaths.map(zp => (
                  <path
                    key={zp.i}
                    d={zp.d}
                    className={`sc-zone ${sector === zp.i ? 'sc-active' : ''}`}
                    onClick={() => selectSector(zp.i)}
                  />
                ))}
              </g>
              <g>
                {zonePaths.map(zp => zp.label && (
                  <text key={`l-${zp.i}`} x={zp.labelX} y={zp.labelY} className="sc-zone-label">
                    {zp.label}
                  </text>
                ))}
              </g>
              <text className="sc-side-label" x="12"  y="144">{hand === 'RH' ? 'LEG' : 'OFF'}</text>
              <text className="sc-side-label" x="268" y="144">{hand === 'RH' ? 'OFF' : 'LEG'}</text>
              <line className="sc-pitch" x1="140" y1="118" x2="140" y2="162" strokeWidth="2"/>
              <circle cx="140" cy="140" r="5" className="sc-batter"/>
              <text x="140" y="14"  fill="var(--sc-text-faint)" fontSize="10" textAnchor="middle" letterSpacing="1.5" fontWeight="700">↑ BOWLER</text>
              <text x="140" y="274" fill="var(--sc-text-faint)" fontSize="10" textAnchor="middle" letterSpacing="1.5" fontWeight="700">KEEPER</text>
            </svg>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: -4, marginBottom: 8 }}>
          <button className={`sc-chip ${hand === 'RH' ? 'sc-active' : ''}`} onClick={() => setHand('RH')}>Right-handed</button>
          <button className={`sc-chip ${hand === 'LH' ? 'sc-active' : ''}`} onClick={() => setHand('LH')}>Left-handed</button>
        </div>

        {sector !== null && (() => {
          // POSITION row — built-in inner/circle/boundary/extras plus any
          // admin-added custom positions for this slice (see commentary.js).
          const chips = sectorChips(sector);
          return (
            <div className="sc-depth-row">
              <div className="sc-depth-label">POSITION</div>
              {chips.map((opt, idx) => {
                const isActive = (ring === opt.ring) && (sideHint === opt.sideHint);
                return (
                  <button
                    key={idx}
                    className={`sc-depth-chip ${isActive ? 'sc-active' : ''}`}
                    onClick={() => { setRing(opt.ring); setSideHint(opt.sideHint); }}
                  >
                    <span className="sc-depth-name">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {(delivery.kind === 'bat' || (delivery.kind === 'nb' && delivery.offTheBat > 0)) && (
          <>
            <h4>Shot type</h4>
            <div className="sc-chip-row">
              {(() => {
                // Filter shots to those typical for the tapped direction
                // (built-ins + custom shots that list this sector), unless
                // "+ other" was tapped — then show the full merged list.
                const filter = (sector !== null && !showAllShots)
                  ? new Set(shotsForSector(sector))
                  : null;
                const all = shotTypeList();
                const visible = filter
                  ? all.filter(s => filter.has(s.key))
                  : all;
                return visible.map(s => (
                  <button
                    key={s.key}
                    className={`sc-chip sc-gold ${shotType === s.key ? 'sc-active' : ''}`}
                    onClick={() => setShotType(s.key)}
                  >
                    {s.label}
                  </button>
                ));
              })()}
              {sector !== null && !showAllShots && (
                <button
                  className="sc-chip"
                  onClick={() => setShowAllShots(true)}
                  title="Show all shot types"
                >
                  + other
                </button>
              )}
            </div>
          </>
        )}

        <h4>Fielder</h4>
        <div className="sc-chip-row">
          <button className={`sc-chip ${fielderId === '' ? 'sc-active' : ''}`} onClick={() => setFielder('')}>— none —</button>
          {bowlingSquad.map(s => (
            <button
              key={s.member_id}
              className={`sc-chip ${fielderId === s.member_id ? 'sc-active' : ''}`}
              onClick={() => setFielder(s.member_id)}
            >
              {membersById.get(s.member_id)?.display_name || ''}
            </button>
          ))}
        </div>

        <h4>Incident <span style={{ fontWeight: 500, color: 'var(--sc-text-mute)', textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>— optional</span></h4>
        <div className="sc-chip-row">
          {INCIDENTS.map(i => (
            <button
              key={i.v}
              className={`sc-chip ${i.v === 'none' ? '' : 'sc-danger'} ${incident === i.v ? 'sc-active' : ''}`}
              onClick={() => setIncident(i.v)}
            >
              {i.label}
            </button>
          ))}
        </div>

        <h4>Auto commentary preview <span style={{ fontWeight: 500, color: 'var(--sc-text-mute)', textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>— editable</span></h4>
        <div className="sc-commentary">
          <div>{comm}</div>
          <textarea
            className="sc-comm-edit"
            value={comm}
            rows={2}
            onChange={(e) => { setComm(e.target.value); setOverridden(true); }}
            placeholder="Override commentary text…"
          />
        </div>
        </div>{/* /sc-modal-body */}
        <div className="sc-modal-actions">
          <button className="sc-submit" onClick={confirm}>Confirm ball</button>
          <button className="sc-cancel" onClick={onSkip}>Skip detail</button>
        </div>
      </div>
    </div>
  );
}
